import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import '@/styles/landing-dark.css';
import { SOCIAL_PROOF } from '@/components/landing/landingData';
import { LandingNav } from '@/components/landing/LandingNav';
import { Hero } from '@/components/landing/Hero';
import { LogoRibbon, MetricsBand, PainPoints, ModulesBento, HowItWorks } from '@/components/landing/SectionsA';
import { AISection, Integrations, Security, Testimonials, Comparison } from '@/components/landing/SectionsB';
import { Pricing, FAQ } from '@/components/landing/Pricing';
import { FinalCTA, LandingFooter, FloatingBits } from '@/components/landing/Footer';

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showCookie, setShowCookie] = useState(false);
  const [exitIntent, setExitIntent] = useState(false);
  const [toast, setToast] = useState(null);
  const [toastExiting, setToastExiting] = useState(false);
  const [announcement, setAnnouncement] = useState(true);
  const toastIndex = useRef(0);

  useEffect(() => {
    if (!loading && user) navigate('/dashboard', { replace: true });
  }, [user, loading, navigate]);

  const goStart = (email) => navigate(user ? '/dashboard' : `/auth${email ? `?email=${encodeURIComponent(email)}` : ''}`);
  const goLogin = () => navigate('/auth');
  const goDemo = () => setExitIntent(true);
  const goContact = () => { window.location.href = 'mailto:sales@woxora.in?subject=Woxora Enterprise'; };

  useEffect(() => {
    const onScroll = () => {
      const top = window.scrollY;
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(h > 0 ? (top / h) * 100 : 0);
      setShowBackToTop(top > 500);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('woxora_cookie_consent')) {
      const t = setTimeout(() => setShowCookie(true), 2000);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.clientY < 5 && !localStorage.getItem('woxora_exit_shown')) {
        localStorage.setItem('woxora_exit_shown', '1');
        setExitIntent(true);
      }
    };
    document.addEventListener('mouseout', handler);
    return () => document.removeEventListener('mouseout', handler);
  }, []);

  useEffect(() => {
    const show = () => {
      setToastExiting(false);
      setToast(SOCIAL_PROOF[toastIndex.current++ % SOCIAL_PROOF.length]);
      setTimeout(() => setToastExiting(true), 3500);
      setTimeout(() => setToast(null), 4000);
    };
    const first = setTimeout(show, 8000);
    const interval = setInterval(show, 30000);
    return () => { clearTimeout(first); clearInterval(interval); };
  }, []);

  useEffect(() => {
    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://embed.tawk.to/6541b0f8f2439e1631e84a77/1he46lbsg';
    s.charset = 'UTF-8';
    s.setAttribute('crossorigin', '*');
    document.head.appendChild(s);
    return () => { try { document.head.removeChild(s); } catch (e) { /* noop */ } };
  }, []);

  const onCookie = (level) => { localStorage.setItem('woxora_cookie_consent', level); setShowCookie(false); };

  return (
    <div className="ld min-h-screen" data-testid="landing-page">
      <div className="ld-progress" style={{ width: `${scrollProgress}%` }} data-testid="scroll-progress" />
      <LandingNav user={user} onLogin={goLogin} onStart={() => goStart()} announcement={announcement} onCloseAnnouncement={() => setAnnouncement(false)} />
      <main>
        <Hero onStart={() => goStart()} onDemo={goDemo} />
        <LogoRibbon />
        <MetricsBand />
        <PainPoints />
        <ModulesBento />
        <HowItWorks />
        <AISection />
        <Integrations />
        <Security />
        <Testimonials />
        <Comparison />
        <Pricing onStart={() => goStart()} onContact={goContact} />
        <FAQ />
        <FinalCTA onStart={goStart} />
      </main>
      <LandingFooter />
      <FloatingBits
        toast={toast} toastExiting={toastExiting} showBackToTop={showBackToTop}
        showCookie={showCookie} onCookie={onCookie}
        exitIntent={exitIntent} onCloseExit={() => setExitIntent(false)}
      />
    </div>
  );
}
